import React from 'react';
import { Courier } from '@/services/types';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ExternalLink, Trash2 } from 'lucide-react';
import { CourierSocialBadges } from './SocialBadges';
import { format, fromUnixTime } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface PaginationConfig {
  page: number;
  pageSize: number;
  totalPages: number;
  total: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

interface CouriersTableProps {
  couriers: Courier[];
  loading: boolean;
  onRowClick: (courier: Courier) => void;
  onDeleteCourier?: (courier: Courier) => Promise<void>;
  pagination?: PaginationConfig;
}

export const CouriersTable: React.FC<CouriersTableProps> = ({ 
  couriers, 
  loading, 
  onRowClick, 
  onDeleteCourier
}) => {
  const formatUnixDate = (timestamp?: number | null) => {
    if (!timestamp) return '—';
    try {
      return format(fromUnixTime(timestamp), 'dd.MM.yyyy', { locale: ru });
    } catch (error) {
      console.error('Ошибка форматирования даты:', error, timestamp);
      return '—';
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Имя',
      cell: (courier: Courier) => (
        <div className="flex items-center space-x-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={courier.avatar} alt={courier.name} />
            <AvatarFallback>{courier.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span>{courier.name}</span>
        </div>
      ),
      sortable: true
    },
    {
      key: 'tariff',
      header: 'Тариф',
      cell: (courier: Courier) => courier.tariff || '—',
      sortable: true
    },
    {
      key: 'deal_number',
      header: 'Сделка',
      cell: (courier: Courier) => (
        <div className="flex items-center space-x-2">
          <span>{courier.deal_number || '—'}</span>
          {courier.amo_deal_id && (
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <ExternalLink className="h-3 w-3" />
            </Button>
          )}
        </div>
      ),
      sortable: true
    },
    {
      key: 'rental_start_ts',
      header: 'Период аренды',
      cell: (courier: Courier) => (
        <div className="text-xs">
          <div>{formatUnixDate(courier.rental_start_ts)} — </div>
          <div>{courier.rental_end_human || formatUnixDate(courier.rental_end_ts)}</div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'status',
      header: 'Статус',
      cell: (courier: Courier) => <StatusBadge status={courier.status} type="courier" />,
      sortable: true
    },
    {
      key: 'social',
      header: 'Каналы',
      cell: (courier: Courier) => (
        <CourierSocialBadges 
          telegram_id={courier.telegram_id} 
          avito_id={courier.avito_id} 
          vk_id={courier.vk_id} 
        />
      ),
      sortable: false
    },
    {
      key: 'actions',
      header: 'Действия',
      cell: (courier: Courier) => (
        <div className="flex items-center space-x-2">
          {onDeleteCourier && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Удалить клиента</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>Вы уверены, что хотите удалить клиента <strong>{courier.name}</strong>?</p>
                    <p>Будут безвозвратно удалены:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>История отслеживания велосипедов</li>
                      <li>Все платежи клиента</li>
                      <li>Активные подписки</li>
                      <li>История аренд</li>
                      <li>Профиль клиента</li>
                    </ul>
                    <p className="text-destructive font-medium">Это действие нельзя отменить!</p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCourier(courier);
                    }}
                  >
                    Удалить
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      ),
      sortable: false
    },
  ];

  return (
    <>
      {loading ? (
        <div className="animate-pulse text-center p-4">Загрузка данных о клиентах...</div>
      ) : (
        <DataTable
          data={couriers}
          columns={columns}
          searchable
          searchPlaceholder="Поиск клиентов..."
          onRowClick={onRowClick}
        />
      )}
    </>
  );
};
